import { apiClient } from "@/lib/api";

const PRICING_BASE = "/v1/public/pricing";

const LAB_PLAN_OVERRIDES = {
  freemium: {
    name: "Freemium",
    description: "for labs that want to try things out",
    monthlyFee: 0,
    slipCapacity: 20,
    storageGb: 5,
    adminSeats: 1,
    userSeats: 5,
    marketingIncludes:
      "20 slips per cycle, 5 GB storage, 1 admin seat, up to 5 user seats.",
  },
  solo: {
    name: "Solo",
    description: "for dental laboratories",
    monthlyFee: 99,
    slipCapacity: 120,
    storageGb: 10,
    adminSeats: 2,
    userSeats: 5,
    marketingIncludes:
      "120 slips per cycle, 10 GB storage, 2 admin seats, up to 5 user seats.",
  },
  business_plus: {
    name: "Business Plus",
    description: "best value, for dental laboratories",
    monthlyFee: 549,
    slipCapacity: 500,
    storageGb: 20,
    adminSeats: 10,
    userSeats: 30,
    badgeLabel: "Best value",
    marketingIncludes:
      "500 slips per cycle, 20 GB storage, 10 admin seats, up to 30 user seats.",
  },
};

function toPlanKey(plan = {}) {
  const candidates = [plan.slug, plan.code, plan.plan_code, plan.name, plan.id]
    .filter(Boolean)
    .map((value) =>
      String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
    );

  for (const value of candidates) {
    if (value.includes("freemium")) return "freemium";
    if (value === "solo" || value.includes("solo_lab")) return "solo";
    if (value.includes("business_plus") || value.includes("businessplus")) {
      return "business_plus";
    }
  }

  return null;
}

function applyLabPlanOverrides(plan = {}) {
  const key = toPlanKey(plan);
  if (!key || !LAB_PLAN_OVERRIDES[key]) return plan;

  const override = LAB_PLAN_OVERRIDES[key];
  const existingPrices = plan?.pricing?.prices || [];
  const hasMonthlyPrice = existingPrices.some((price) => price?.frequency === "monthly");
  const monthlyPrice = {
    frequency: "monthly",
    price: String(override.monthlyFee),
    currency: "USD",
    is_default: true,
  };

  return {
    ...plan,
    name: override.name,
    description: override.description,
    badge_label: override.badgeLabel ?? plan.badge_label,
    monthly_fee: override.monthlyFee,
    slip_capacity: override.slipCapacity,
    storage_included_gb: override.storageGb,
    included_admins: override.adminSeats,
    max_user_seats: override.userSeats,
    marketing_includes: override.marketingIncludes,
    pricing: {
      ...(plan.pricing || {}),
      currency: "USD",
      prices: hasMonthlyPrice
        ? existingPrices.map((price) =>
            price?.frequency === "monthly"
              ? { ...price, ...monthlyPrice }
              : price
          )
        : [...existingPrices, monthlyPrice],
    },
    feature_limits: {
      ...(plan.feature_limits || {}),
      slip_capacity: override.slipCapacity,
      included_storage_gb: override.storageGb,
      max_admin_seats: override.adminSeats,
      max_user_seats: override.userSeats,
    },
  };
}

/**
 * Public pricing API (no auth. Lab plans for the marketing site.
 */
export const pricingRepository = {
  async getPlans() {
    const { data } = await apiClient.get(`${PRICING_BASE}/plans`);
    const plans = data?.data ?? [];
    return plans.map(applyLabPlanOverrides);
  },

  async getPlan(id) {
    const { data } = await apiClient.get(`${PRICING_BASE}/plans/${id}`);
    return data?.data ?? null;
  },

  async getPlanFeatures(id) {
    const { data } = await apiClient.get(`${PRICING_BASE}/plans/${id}/features`);
    return data?.data ?? [];
  },
};
